// Persistent pinned-today store. Survives reloads via localStorage so a
// hard reload mid-tap doesn't lose the star state. Mirrors `optimistic.ts`.
// Entries auto-expire after MAX_AGE_MS (36h) so stale pins clear on their own.

import { writable } from "svelte/store";

const KEY = "surface:pinned-today";
const MAX_AGE_MS = 36 * 60 * 60 * 1000; // 36h, mirrors server-side pin TTL

export type PinEntry = { day: string; evening: boolean; ts: number };
type PinMap = Record<string, PinEntry>;

function load(): PinMap {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return prune(parsed);
  } catch {
    return {};
  }
}

function prune(m: PinMap): PinMap {
  const cutoff = Date.now() - MAX_AGE_MS;
  const out: PinMap = {};
  for (const [k, v] of Object.entries(m)) {
    if (
      v &&
      typeof v === "object" &&
      typeof (v as PinEntry).ts === "number" &&
      (v as PinEntry).ts >= cutoff &&
      typeof (v as PinEntry).day === "string"
    ) {
      out[k] = {
        day: (v as PinEntry).day,
        evening: !!(v as PinEntry).evening,
        ts: (v as PinEntry).ts,
      };
    }
  }
  return out;
}

function save(m: PinMap) {
  try { localStorage.setItem(KEY, JSON.stringify(m)); } catch {}
}

function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

const initial = load();
save(initial);

export const pinnedToday = writable<PinMap>(initial);

export function markPinned(fingerprint: string, evening = false, day?: string) {
  pinnedToday.update((m) => {
    const next = {
      ...m,
      [fingerprint]: { day: day ?? todayISO(), evening, ts: Date.now() },
    };
    save(next);
    return next;
  });
}

export function markUnpinned(fingerprint: string) {
  pinnedToday.update((m) => {
    if (!(fingerprint in m)) return m;
    const next = { ...m };
    delete next[fingerprint];
    save(next);
    return next;
  });
}

// Reconcile against the latest snapshot pins. The server-confirmed pins become
// authoritative — drop locals the server already knows about, and re-add
// confirmed pins as authoritative entries (so we render correctly even when no
// local action happened in this session).
export function reconcile(snapshotPins: { fingerprint: string; day: string; evening: boolean }[]) {
  pinnedToday.update((m) => {
    const next: PinMap = { ...m };
    let changed = false;
    const confirmed = new Set<string>();
    for (const p of snapshotPins) {
      confirmed.add(p.fingerprint);
      const existing = next[p.fingerprint];
      if (
        !existing ||
        existing.day !== p.day ||
        existing.evening !== p.evening
      ) {
        next[p.fingerprint] = { day: p.day, evening: p.evening, ts: Date.now() };
        changed = true;
      }
    }
    // Drop any local entries that are stale — i.e. not in the confirmed set
    // AND older than 60s. Keep very recent locals so we don't fight an
    // in-flight pin/unpin against a stale snapshot.
    const stale = Date.now() - 60_000;
    for (const fp of Object.keys(next)) {
      if (!confirmed.has(fp) && next[fp].ts < stale) {
        delete next[fp];
        changed = true;
      }
    }
    if (changed) save(next);
    return next;
  });
}

export function isPinned(m: PinMap, fingerprint: string): boolean {
  return fingerprint in m;
}

export function isEvening(m: PinMap, fingerprint: string): boolean {
  return !!m[fingerprint]?.evening;
}
